import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { apiClient } from '../../lib/api-client';

import { useAuth } from './auth-context';

interface LocationState {
  from?: { pathname: string };
}

export function LoginPage() {
  const { login, loginWith2fa } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = (location.state as LocationState | null)?.from?.pathname ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Mode « mot de passe oublié »
  const [forgot, setForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (challengeToken) {
        await loginWith2fa(challengeToken, code);
        navigate(redirectTo, { replace: true });
        return;
      }
      const result = await login({ email, password });
      if (result.kind === 'requires2fa') {
        setChallengeToken(result.challengeToken);
      } else if (result.kind === 'requiresSetup') {
        navigate('/auth/setup-2fa', { state: { setup: result.setup }, replace: true });
      } else {
        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        t('auth.errorGeneric');
      setError(message);
    } finally {
      setBusy(false);
    }
  }

  async function handleForgot(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await apiClient.post('/auth/password/forgot', { email });
    } catch {
      // L'API répond toujours 200 (anti-énumération) ; on reste neutre même en cas d'erreur réseau.
    } finally {
      setBusy(false);
      setForgotSent(true);
    }
  }

  function resetToLogin() {
    setForgot(false);
    setForgotSent(false);
    setError(null);
    setPassword('');
  }

  const subtitle = forgot
    ? 'Mot de passe oublié'
    : challengeToken
      ? t('auth.twoFaTitle')
      : t('auth.loginTitle');

  return (
    <main className="flex min-h-screen flex-col bg-cream">
      <div className="flex flex-1 items-center justify-center px-4 py-8">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-lg ring-1 ring-slate-200">
          <header className="mb-8 text-center">
            <h1>
              <img
                src="/brand/adweb-systemes.webp"
                alt={`Adweb-Systèmes — ${t('app.name')}`}
                width={640}
                height={276}
                className="mx-auto h-auto w-56"
              />
            </h1>
            <div className="mx-auto mt-3 h-1 w-12 rounded-full bg-accent" />
            <p className="mt-3 text-sm text-slate-500">
              {t('app.tagline')} — {subtitle}
            </p>
          </header>

          {error && (
            <div
              role="alert"
              className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          {/* ───── Mode mot de passe oublié ───── */}
          {forgot ? (
            forgotSent ? (
              <div className="space-y-5">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                  Si un compte existe pour cette adresse, un e-mail contenant un lien de
                  réinitialisation vient d'être envoyé. Le lien est valable 1 heure.
                </div>
                <button
                  type="button"
                  onClick={resetToLogin}
                  className="block w-full text-center text-xs text-slate-500 hover:text-slate-700"
                >
                  Retour à la connexion
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgot} className="space-y-5" noValidate>
                <div>
                  <label
                    htmlFor="forgot-email"
                    className="mb-1 block text-sm font-medium text-slate-700"
                  >
                    {t('auth.email')}
                  </label>
                  <input
                    id="forgot-email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
                  />
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  className="w-full rounded-lg bg-brand px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand/50 disabled:opacity-60"
                >
                  {busy ? 'Envoi…' : 'Envoyer le lien de réinitialisation'}
                </button>
                <button
                  type="button"
                  onClick={resetToLogin}
                  className="block w-full text-center text-xs text-slate-500 hover:text-slate-700"
                >
                  Retour à la connexion
                </button>
              </form>
            )
          ) : (
            /* ───── Mode connexion / 2FA ───── */
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              {!challengeToken && (
                <>
                  <div>
                    <label
                      htmlFor="email"
                      className="mb-1 block text-sm font-medium text-slate-700"
                    >
                      {t('auth.email')}
                    </label>
                    <input
                      id="email"
                      type="email"
                      required
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="password"
                      className="mb-1 block text-sm font-medium text-slate-700"
                    >
                      {t('auth.password')}
                    </label>
                    <input
                      id="password"
                      type="password"
                      required
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
                    />
                  </div>
                </>
              )}

              {challengeToken && (
                <div>
                  <label htmlFor="code" className="mb-1 block text-sm font-medium text-slate-700">
                    {t('auth.twoFaCode')}
                  </label>
                  <input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    required
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-center text-lg tracking-widest focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-lg bg-brand px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand/50 disabled:opacity-60"
              >
                {busy
                  ? t('auth.submitting')
                  : challengeToken
                    ? t('auth.submitTwoFa')
                    : t('auth.submit')}
              </button>

              {!challengeToken && (
                <button
                  type="button"
                  onClick={() => {
                    setForgot(true);
                    setError(null);
                  }}
                  className="block w-full text-center text-xs text-slate-500 hover:text-slate-700"
                >
                  Mot de passe oublié ?
                </button>
              )}

              {challengeToken && (
                <button
                  type="button"
                  onClick={() => {
                    setChallengeToken(null);
                    setCode('');
                    setError(null);
                  }}
                  className="block w-full text-center text-xs text-slate-500 hover:text-slate-700"
                >
                  {t('auth.backToLogin')}
                </button>
              )}
            </form>
          )}
        </div>
      </div>
      <footer className="border-t border-slate-200/60 bg-cream py-4">
        <nav className="mx-auto flex max-w-3xl flex-wrap items-center justify-center gap-x-4 gap-y-1 px-4 text-xs text-slate-500">
          <Link to="/legal/mentions-legales" className="hover:text-slate-900">
            Mentions légales
          </Link>
          <Link to="/legal/cgv" className="hover:text-slate-900">
            CGV
          </Link>
          <Link to="/legal/cgu" className="hover:text-slate-900">
            CGU
          </Link>
          <Link to="/legal/confidentialite" className="hover:text-slate-900">
            Confidentialité
          </Link>
          <Link to="/legal/cookies" className="hover:text-slate-900">
            Cookies
          </Link>
        </nav>
      </footer>
    </main>
  );
}
